import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form, InputNumber, Switch, Button, Alert, Descriptions } from 'antd'
import {
  shippingFeeUrl,
  patchRequest,
  successNotification,
  errorNotification
} from '../../../config'
import { buildShippingFeePayload, isValidAmount } from './utils'

// Edit a shipping fee's freight + insurance amounts and clear the review flag. Currencies
// are set by onboarding (USD) and shown read-only here. PATCH only (the row already exists).
const ShippingFeeForm = ({ record, handleDone }) => {
  const [loading, setLoading] = useState(false)

  const onFinish = async values => {
    if (!isValidAmount(values.shippingFeeAmount) || !isValidAmount(values.insuranceAmount)) {
      errorNotification('Enter valid non-negative amounts')
      return
    }
    const payload = buildShippingFeePayload(values)
    const response = await patchRequest(
      shippingFeeUrl(record.id),
      payload,
      'shipping fee',
      setLoading
    )
    if (response.success) {
      successNotification('Shipping fee updated')
      handleDone && handleDone()
    } else {
      errorNotification(
        response.error?.response?.data?.message || 'Could not save the shipping fee, retry.'
      )
    }
  }

  return (
    <Form
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        shippingFeeAmount: record?.shippingFeeAmount ? Number(record.shippingFeeAmount) : 0,
        insuranceAmount: record?.insuranceAmount ? Number(record.insuranceAmount) : 0,
        flaggedForReview: record?.flaggedForReview ?? false
      }}
    >
      {record?.isAiEstimated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="AI-estimated figures"
          description="These freight/insurance amounts were estimated by the assistant for a new market. Verify them, correct if needed, and turn off 'Flagged for review' once confirmed."
        />
      )}

      <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Origin">
          {record?.origin?.country || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Body type">
          {record?.bodyType?.name || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Currency">
          {record?.shippingFeeCurrency || 'USD'}
        </Descriptions.Item>
      </Descriptions>

      <Form.Item
        name="shippingFeeAmount"
        label={`Freight (${record?.shippingFeeCurrency || 'USD'})`}
        rules={[{ required: true, message: 'Freight amount is required' }]}
      >
        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        name="insuranceAmount"
        label={`Insurance (${record?.insuranceCurrency || 'USD'})`}
        rules={[{ required: true, message: 'Insurance amount is required' }]}
      >
        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        name="flaggedForReview"
        label="Flagged for review (turn off once verified)"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block>
          Save changes
        </Button>
      </Form.Item>
    </Form>
  )
}

ShippingFeeForm.propTypes = {
  record: PropTypes.object,
  handleDone: PropTypes.func
}

export default ShippingFeeForm
